import { useQuery } from "react-query";
import {
    AddSupportTicket,
    DeleteSupportTicket,
    GetAllTickets,
    GetSupportTicketById,
    GetSupportTickets,
    UpdateSupportTicket,
    UpdateTicketStatus,
    AssignTicket,
    GetSupportTicketInteractions,
    AddInteraction,
    AcknowledgeSupportTicketResolution,
    SubmitSupportTicketFeedback,
    CreateTicketByAdmin,
    GetTicketDetails,
    UpdateTicketDueDate
} from "../services/backend";
import { useMutation, useQueryClient } from "react-query";
import { Notify, NotifyError } from "../utils/functions";


export function useGetSupportTickets(filters = {}) {
    return useQuery(['support-tickets', filters], () => GetSupportTickets(filters), {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        select: (data) => {
            return data?.data || [];
        },
        onError: (error) => {
            NotifyError("Failed to fetch support tickets", error);
            console.error("Error fetching support tickets:", error);
        }
    });
}

export function useGetSupportTicketById(ticketId, refetchWindow = 5 * 60 * 1000) {
    return useQuery(
        ['support-ticket', ticketId],
        () => GetSupportTicketById(ticketId),
        {
            enabled: !!ticketId,
            staleTime: refetchWindow,
            refetchOnWindowFocus: true,
            refetchInterval: refetchWindow,
            select: (data) => {
                return data?.data || {};
            },
            onError: (error) => {
                NotifyError("Failed to fetch support ticket details", error);
                console.error("Error fetching support ticket details:", error);
            }
        }
    );
}

export function useAddSupportTicket() {
    const queryClient = useQueryClient();
    return useMutation(AddSupportTicket, {
        onSuccess: () => {
            Notify("Support ticket added successfully", "success");
            queryClient.invalidateQueries('support-tickets');
        },
        onError: (error) => {
            NotifyError("Failed to add support ticket", error);
            console.error("Error adding support ticket:", error);
        }
    });
}

export function useUpdateSupportTicket() {
    const queryClient = useQueryClient();
    return useMutation(({ ticketId, data }) => UpdateSupportTicket(ticketId, data), {
        onSuccess: () => {
            Notify("Support ticket updated successfully", "success");
            queryClient.invalidateQueries('support-tickets');
        },
        onError: (error) => {
            NotifyError("Failed to update support ticket", error);
            console.error("Error updating support ticket:", error);
        }
    });
}

export function useDeleteSupportTicket() {
    const queryClient = useQueryClient();
    return useMutation(DeleteSupportTicket, {
        onSuccess: () => {
            Notify("Support ticket deleted successfully", "success");
            queryClient.invalidateQueries('support-tickets');
        },
        onError: (error) => {
            NotifyError("Failed to delete support ticket", error);
            console.error("Error deleting support ticket:", error);
        }
    });
}

export function useGetAllTickets(filters = {}) {
    return useQuery(['all-tickets'], () => GetAllTickets(filters), {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        select: (data) => {
            return data?.data || [];
        },
        onError: (error) => {
            NotifyError("Failed to fetch tickets", error);
            console.error("Error fetching tickets:", error);
        }
    });
}

export function useUpdateTicketStatus() {
    const queryClient = useQueryClient();
    return useMutation(({ ticketId, data }) => UpdateTicketStatus(ticketId, data), {
        onSuccess: () => {
            Notify("Ticket status updated successfully", "success");
            queryClient.invalidateQueries('all-tickets');
        },
        onError: (error) => {
            NotifyError("Failed to update ticket status", error);
            console.error("Error updating ticket status:", error);
        }
    });
}

export function useAssignTicket() {
    const queryClient = useQueryClient();
    return useMutation(({ ticketId, data }) => AssignTicket(ticketId, data), {
        onSuccess: () => {
            Notify("Ticket assigned successfully", "success");
            queryClient.invalidateQueries('all-tickets');
        },
        onError: (error) => {
            NotifyError("Failed to assign ticket", error);
            console.error("Error assigning ticket:", error);
        }
    });
}

export function useGetSupportTicketInteractions(ticketId) {
    return useQuery(['support-ticket-interactions', ticketId], () => GetSupportTicketInteractions(ticketId), {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        select: (data) => {
            return data?.data || [];
        },
        onError: (error) => {
            NotifyError("Failed to fetch ticket status change interactions for user", error);
            console.error("Error fetching ticket status change interactions for user:", error);
        }
    });
}

export const useAddSupportTicketInteraction = () => {
    return useMutation(({ ticketId, data }) => AddInteraction(ticketId, data), {
        onSuccess: () => {
            Notify("Ticket interaction added successfully", "success");
        },
        onError: (error) => {
            NotifyError("Failed to add ticket interaction", error);
            console.error("Error adding ticket interaction:", error);
        }
    });
}

export const useAddSupportTicketResolutionAcknowledgment = () => {
    return useMutation(({ ticketId, data }) => AcknowledgeSupportTicketResolution(ticketId, data), {
        onSuccess: () => {
            Notify("Ticket resolution acknowledgment added successfully", "success");
        },
        onError: (error) => {
            NotifyError("Failed to add ticket resolution acknowledgment", error);
            console.error("Error adding ticket resolution acknowledgment:", error);
        }
    });
}

export const useSubmitSupportTicketFeedback = () => {
    return useMutation(({ ticketId, data }) => SubmitSupportTicketFeedback(ticketId, data), {
        onSuccess: () => {
            Notify("Ticket feedback submitted successfully", "success");
        },
        onError: (error) => {
            NotifyError("Failed to submit ticket feedback", error);
            console.error("Error submitting ticket feedback:", error);
        }
    });
}

export const useCreateTicketByAdmin = () => {
    const queryClient = useQueryClient();
    return useMutation(CreateTicketByAdmin, {
        onSuccess: () => {
            queryClient.invalidateQueries('all-tickets');
            Notify("Ticket created successfully", "success");
        },
        onError: (error) => {
            NotifyError("Failed to create ticket", error);
            console.error("Error creating ticket:", error);
        }
    });
}

export function useGetSupportTicketDetails(ticketId) {
    return useQuery(['support-ticket-details', ticketId], () => GetTicketDetails(ticketId), {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        select: (data) => {
            return data?.data || {};
        },
        onError: (error) => {
            NotifyError("Failed to fetch support ticket details", error);
            console.error("Error fetching support ticket details:", error);
        }
    });
}

export function useUpdateTicketDueDate() {
    const queryClient = useQueryClient();
    return useMutation(({ ticketId, data }) => UpdateTicketDueDate(ticketId, data), {
        onSuccess: () => {
            Notify("Ticket due date updated successfully", "success");
            queryClient.invalidateQueries('all-tickets');
        },
        onError: (error) => {
            NotifyError("Failed to update ticket due date", error);
            console.error("Error updating ticket due date:", error);
        }
    });
}